import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  CONTRIBUTION_CONFIDENCES,
  DEFAULT_SCORING_SCHEME,
  WINE_COLORS,
  WINE_LETTERS,
  computeTotal,
  maxTotal,
  contributionWineKey,
  type ContributionAttendee,
  type ContributionScore,
  type ContributionWine,
  type HistoryContributionPayload,
} from '@gwc/shared';
import { historyApi, type ContributionFileRow } from '../api/historyContributions';
import { ApiError } from '../lib/api';
import { Combobox } from './Combobox';
import { FileDropzone } from './FileDropzone';
import { PersonPicker } from './PersonPicker';

const CONFIDENCE_LABEL = { certain: 'Certain', fairly_sure: 'Fairly sure', vague: 'Hazy — best guess' } as const;
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const num = (v: string): number | null => (v.trim() === '' ? null : Number(v));
const str = (v: string): string | null => (v.trim() === '' ? null : v);
const show = (v: number | null | undefined) => (v == null ? '' : String(v));

/**
 * The scorecard's own bounds, per line — the same ones the server checks
 * (contributionScoreSchema). A total stands in only when the parts are unknown.
 */
type Range = { min: number; max: number };
const SCHEME = DEFAULT_SCORING_SCHEME;
const SCORE_PARTS = [
  { key: 'sightNose', label: 'Sight/Nose', range: SCHEME.sightNose },
  { key: 'taste', label: 'Taste', range: SCHEME.taste },
  { key: 'overall', label: 'Overall', range: SCHEME.overall },
] as const;
const TOTAL_RANGE: Range = {
  min: SCHEME.sightNose.min + SCHEME.taste.min + SCHEME.overall.min,
  max: maxTotal(SCHEME),
};
const SCORES_HINT = `Anyone's scores — yours or from a scanned card. ${SCORE_PARTS.map((f) => `${f.label} ${f.range.min}–${f.range.max}`).join(', ')}, or just a total (${TOTAL_RANGE.min}–${TOTAL_RANGE.max}).`;

/** Why a typed score wouldn't save, or null when it's fine (or blank). */
function scoreIssue(v: number | null | undefined, range: Range, quarterPoints = true): string | null {
  if (v == null) return null;
  if (!Number.isFinite(v) || v < range.min || v > range.max) return `Between ${range.min} and ${range.max}`;
  if (quarterPoints && !Number.isInteger(v * 4)) return 'Quarter points only (e.g. 2.25)';
  return null;
}

function scoreHasIssue(s: ContributionScore): boolean {
  return (
    SCORE_PARTS.some((f) => scoreIssue(s[f.key], f.range)) ||
    (computeTotal({ sightNose: s.sightNose ?? null, taste: s.taste ?? null, overall: s.overall ?? null }) == null &&
      !!scoreIssue(s.total, TOTAL_RANGE, false))
  );
}

const invalidField = (issue: string | null) => (issue ? 'field border-danger focus:border-danger' : 'field');

function Section({ title, hint, children, defaultOpen = true }: { title: string; hint?: string; children: React.ReactNode; defaultOpen?: boolean }) {
  return (
    <details open={defaultOpen} className="card group">
      <summary className="cursor-pointer list-none">
        <span className="text-sm uppercase tracking-[0.15em] text-gold-400">{title}</span>
        {hint && <span className="ml-3 text-xs text-cream-300/50">{hint}</span>}
      </summary>
      <div className="mt-4 space-y-3">{children}</div>
    </details>
  );
}

function L({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-xs text-cream-300/70">
      <span className="mb-1 block">{label}</span>
      {children}
    </label>
  );
}

/**
 * The template form. Every field is optional by design — the banner says so —
 * and the payload autosaves a moment after each change.
 */
export function ContributionForm({
  contributionId,
  initial,
  files,
  readOnly,
  onSaved,
}: {
  contributionId: string;
  initial: HistoryContributionPayload;
  files: ContributionFileRow[];
  readOnly?: boolean;
  onSaved?: (p: HistoryContributionPayload) => void;
}) {
  const [p, setP] = useState<HistoryContributionPayload>(initial);
  const [dirty, setDirty] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const latest = useRef(p);
  latest.current = p;

  const save = useMutation({
    mutationFn: (payload: HistoryContributionPayload) => historyApi.update(contributionId, payload),
    onSuccess: (row) => {
      setSavedAt(new Date());
      setDirty(false);
      onSaved?.(row.payload);
    },
  });

  // A score outside the card's bounds would fail the whole save, so hold the
  // autosave (and say why) until it's fixed rather than losing everything else.
  const scoresInvalid = (p.scores ?? []).some(scoreHasIssue);

  // Autosave 1.5 s after the last keystroke.
  useEffect(() => {
    if (!dirty || readOnly || scoresInvalid) return;
    const t = setTimeout(() => save.mutate(latest.current), 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p, dirty, readOnly, scoresInvalid]);

  // Don't lose an unsaved edit to an accidental tab close.
  useEffect(() => {
    if (!dirty) return;
    const h = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  const patch = (fn: (prev: HistoryContributionPayload) => HistoryContributionPayload) => {
    if (readOnly) return;
    setP(fn);
    setDirty(true);
  };
  const ev = p.event ?? {};
  const setEv = (k: keyof NonNullable<HistoryContributionPayload['event']>, v: unknown) =>
    patch((prev) => ({ ...prev, event: { ...(prev.event ?? {}), [k]: v } }));

  const wines = p.wines ?? [];
  const setWine = (i: number, w: Partial<ContributionWine>) =>
    patch((prev) => ({ ...prev, wines: (prev.wines ?? []).map((x, j) => (j === i ? { ...x, ...w } : x)) }));
  const addWine = () =>
    patch((prev) => {
      const used = new Set((prev.wines ?? []).map((w) => w.letter).filter(Boolean));
      const letter = WINE_LETTERS.find((l) => !used.has(l)) ?? null;
      return { ...prev, wines: [...(prev.wines ?? []), { clientKey: crypto.randomUUID().slice(0, 12), letter }] };
    });
  const removeWine = (i: number) => patch((prev) => ({ ...prev, wines: (prev.wines ?? []).filter((_, j) => j !== i) }));

  const attendees = p.attendees ?? [];
  const setAttendee = (i: number, a: Partial<ContributionAttendee> | null) =>
    patch((prev) => ({
      ...prev,
      attendees: a === null ? (prev.attendees ?? []).filter((_, j) => j !== i) : (prev.attendees ?? []).map((x, j) => (j === i ? { ...x, ...a } : x)),
    }));

  const scores = p.scores ?? [];
  const setScore = (i: number, s: Partial<ContributionScore> | null) =>
    patch((prev) => ({
      ...prev,
      scores: s === null ? (prev.scores ?? []).filter((_, j) => j !== i) : (prev.scores ?? []).map((x, j) => (j === i ? { ...x, ...s } : x)),
    }));
  const addScore = () => patch((prev) => ({ ...prev, scores: [...(prev.scores ?? []), {}] }));
  const addGrid = () =>
    patch((prev) => {
      const existing = prev.scores ?? [];
      const out = [...existing];
      for (const a of prev.attendees ?? []) {
        if (!a.name && !a.personId) continue;
        for (const [j, w] of (prev.wines ?? []).entries()) {
          const key = contributionWineKey(w, j);
          const dup = existing.some(
            (s) => (s.wineKey === key || (w.letter && s.letter === w.letter)) && ((a.personId && s.person?.personId === a.personId) || (a.name && s.person?.name === a.name)),
          );
          if (!dup) out.push({ person: { personId: a.personId ?? null, name: a.name ?? null }, wineKey: key, letter: w.letter ?? null });
        }
      }
      return { ...prev, scores: out };
    });

  const wineLabel = (w: ContributionWine, i: number) => `${w.letter ?? '?'} · ${w.name || w.producer || `Wine ${i + 1}`}`;
  const disabled = !!readOnly;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2 rounded border border-gold-600/30 bg-gold-500/5 px-4 py-3 text-sm text-cream-100">
        <span>
          <strong className="text-gold-200">Leave blank anything you don't know.</strong> A title and a rough year help, but even a single photo is
          worth sending — a Records Admin fills in the rest when it's merged.
        </span>
        {!readOnly && (
          <span className="text-xs text-cream-300/60">
            {save.isPending ? 'Saving…' : save.isError ? (
              <span className="text-wine-300">Save failed{save.error instanceof ApiError ? ` — ${save.error.message}` : ''}</span>
            ) : dirty && scoresInvalid ? (
              <span className="text-wine-300">Fix the highlighted score to save</span>
            ) : dirty ? 'Unsaved changes' : savedAt ? `Saved ${savedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'Autosaves as you type'}
          </span>
        )}
      </div>

      <Section title="The tasting" hint="Where and when, as best you remember">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="sm:col-span-2">
            <L label="Title / name of the tasting">
              <input className="field" value={ev.title ?? ''} disabled={disabled} placeholder="e.g. Bordeaux Night, Spring Cabernet…" onChange={(e) => setEv('title', str(e.target.value))} />
            </L>
          </div>
          <L label="Year">
            <input className="field" type="number" min={1900} max={2100} value={show(ev.year)} disabled={disabled} onChange={(e) => setEv('year', num(e.target.value))} />
          </L>
          <div className="grid grid-cols-2 gap-2">
            <L label="Month">
              <select className="field" value={show(ev.month)} disabled={disabled} onChange={(e) => setEv('month', num(e.target.value))}>
                <option value="">?</option>
                {MONTHS.map((m, i) => (
                  <option key={m} value={i + 1}>
                    {m}
                  </option>
                ))}
              </select>
            </L>
            <L label="Day">
              <input className="field" type="number" min={1} max={31} value={show(ev.day)} disabled={disabled} onChange={(e) => setEv('day', num(e.target.value))} />
            </L>
          </div>
          <L label="Venue">
            <Combobox kind="venues" disabled={disabled} value={ev.venue ?? ''} onChange={(v) => setEv('venue', str(v))} placeholder="Restaurant, home, club…" />
          </L>
          <L label="Region / location">
            <input className="field" value={ev.regionLocation ?? ''} disabled={disabled} placeholder="Monterey Peninsula, Napa…" onChange={(e) => setEv('regionLocation', str(e.target.value))} />
          </L>
          <L label="Host">
            <input className="field" value={ev.host ?? ''} disabled={disabled} onChange={(e) => setEv('host', str(e.target.value))} />
          </L>
          <L label="Theme">
            <input className="field" value={ev.theme ?? ''} disabled={disabled} placeholder="Left bank, blind Pinot…" onChange={(e) => setEv('theme', str(e.target.value))} />
          </L>
          <L label="Dress code">
            <input className="field" value={ev.dressCode ?? ''} disabled={disabled} onChange={(e) => setEv('dressCode', str(e.target.value))} />
          </L>
          <L label="Price per head ($)">
            <input className="field" type="number" min={0} step="0.01" value={show(ev.price)} disabled={disabled} onChange={(e) => setEv('price', num(e.target.value))} />
          </L>
          <div className="sm:col-span-2 lg:col-span-4">
            <L label="Menu / food">
              <textarea className="field min-h-[60px]" value={ev.menu ?? ''} disabled={disabled} onChange={(e) => setEv('menu', str(e.target.value))} />
            </L>
          </div>
          <div className="sm:col-span-2 lg:col-span-4">
            <L label="Notes about the tasting">
              <textarea className="field min-h-[60px]" value={ev.notes ?? ''} disabled={disabled} onChange={(e) => setEv('notes', str(e.target.value))} />
            </L>
          </div>
        </div>
      </Section>

      <Section title="Who was there" hint="Members and guests — pick from the records or just type a name">
        {attendees.map((a, i) => (
          <div key={i} className="grid gap-2 sm:grid-cols-[1fr_auto_1fr_auto] sm:items-center">
            <PersonPicker value={a} disabled={disabled} onChange={(v) => setAttendee(i, v ? { personId: v.personId ?? null, name: v.name ?? null } : { personId: null, name: null })} />
            <label className="flex items-center gap-2 text-xs text-cream-300/70">
              <input type="checkbox" checked={!!a.isGuest} disabled={disabled} onChange={(e) => setAttendee(i, { isGuest: e.target.checked })} /> guest
            </label>
            <input className="field" placeholder="Note (optional)" value={a.notes ?? ''} disabled={disabled} onChange={(e) => setAttendee(i, { notes: str(e.target.value) })} />
            {!disabled && (
              <button type="button" className="text-xs text-wine-300 hover:underline" onClick={() => setAttendee(i, null)}>
                remove
              </button>
            )}
          </div>
        ))}
        {!disabled && (
          <button type="button" className="btn-ghost text-sm" onClick={() => patch((prev) => ({ ...prev, attendees: [...(prev.attendees ?? []), {}] }))}>
            + Add a person
          </button>
        )}
      </Section>

      <Section title="The wines" hint="Letters A–T as on the scorecard; leave the letter blank if you don't know it">
        {wines.map((w, i) => (
          <div key={w.clientKey ?? i} className="rounded border border-ink-800 bg-ink-900/30 p-3">
            <div className="grid gap-2 sm:grid-cols-6">
              <L label="Letter">
                <select className="field" value={w.letter ?? ''} disabled={disabled} onChange={(e) => setWine(i, { letter: (e.target.value || null) as ContributionWine['letter'] })}>
                  <option value="">?</option>
                  {WINE_LETTERS.map((l) => (
                    <option key={l} value={l}>
                      {l}
                    </option>
                  ))}
                </select>
              </L>
              <div className="sm:col-span-3">
                <L label="Wine name">
                  <input className="field" value={w.name ?? ''} disabled={disabled} onChange={(e) => setWine(i, { name: str(e.target.value) })} />
                </L>
              </div>
              <L label="Vintage">
                <input className="field" type="number" min={1800} max={2100} value={show(w.vintage)} disabled={disabled} onChange={(e) => setWine(i, { vintage: num(e.target.value) })} />
              </L>
              <L label="Colour">
                <select className="field" value={w.color ?? ''} disabled={disabled} onChange={(e) => setWine(i, { color: (e.target.value || null) as ContributionWine['color'] })}>
                  <option value="">?</option>
                  {WINE_COLORS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </L>
              <div className="sm:col-span-2">
                <L label="Producer">
                  <Combobox kind="producers" disabled={disabled} value={w.producer ?? ''} onChange={(v) => setWine(i, { producer: str(v) })} />
                </L>
              </div>
              <div className="sm:col-span-2">
                <L label="Varietal / type">
                  <Combobox kind="varietals" disabled={disabled} value={w.varietal ?? ''} onChange={(v) => setWine(i, { varietal: str(v) })} />
                </L>
              </div>
              <div className="sm:col-span-2">
                <L label="Region / appellation">
                  <Combobox kind="regions" disabled={disabled} value={w.region ?? ''} onChange={(v) => setWine(i, { region: str(v) })} />
                </L>
              </div>
              <div className="sm:col-span-2">
                <L label="Vineyard">
                  <input className="field" value={w.vineyard ?? ''} disabled={disabled} onChange={(e) => setWine(i, { vineyard: str(e.target.value) })} />
                </L>
              </div>
              <div className="sm:col-span-2">
                <L label="Brought by">
                  <PersonPicker value={w.broughtBy} disabled={disabled} onChange={(v) => setWine(i, { broughtBy: v })} />
                </L>
              </div>
              <L label="Group score">
                <input className="field" type="number" step="0.25" value={show(w.groupScore)} disabled={disabled} onChange={(e) => setWine(i, { groupScore: num(e.target.value) })} />
              </L>
              <L label="Finished #">
                <input className="field" type="number" min={1} max={20} value={show(w.groupRank)} disabled={disabled} onChange={(e) => setWine(i, { groupRank: num(e.target.value) })} />
              </L>
              <div className="sm:col-span-6">
                <L label="Notes about this wine">
                  <input className="field" value={w.notes ?? ''} disabled={disabled} onChange={(e) => setWine(i, { notes: str(e.target.value) })} />
                </L>
              </div>
            </div>
            {!disabled && (
              <button type="button" className="mt-2 text-xs text-wine-300 hover:underline" onClick={() => removeWine(i)}>
                remove wine
              </button>
            )}
          </div>
        ))}
        {!disabled && wines.length < WINE_LETTERS.length && (
          <button type="button" className="btn-ghost text-sm" onClick={addWine}>
            + Add a wine
          </button>
        )}
      </Section>

      <Section title="Scores" hint={SCORES_HINT} defaultOpen={scores.length > 0}>
        {scores.map((s, i) => {
          const computed = computeTotal({ sightNose: s.sightNose ?? null, taste: s.taste ?? null, overall: s.overall ?? null });
          return (
            <div key={i} className="grid gap-2 rounded border border-ink-800 bg-ink-900/30 p-2 sm:grid-cols-[1.4fr_1.2fr_repeat(4,minmax(0,0.6fr))_1.6fr_auto] sm:items-end">
              <L label="Who">
                <PersonPicker value={s.person} disabled={disabled} onChange={(v) => setScore(i, { person: v })} />
              </L>
              <L label="Wine">
                <select
                  className="field"
                  value={s.wineKey ?? (s.letter ? `letter:${s.letter}` : '')}
                  disabled={disabled}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v.startsWith('letter:')) setScore(i, { wineKey: null, letter: v.slice(7) as ContributionScore['letter'] });
                    else {
                      const idx = wines.findIndex((w, j) => contributionWineKey(w, j) === v);
                      setScore(i, { wineKey: v || null, letter: idx >= 0 ? wines[idx]!.letter ?? null : null });
                    }
                  }}
                >
                  <option value="">?</option>
                  {wines.map((w, j) => (
                    <option key={contributionWineKey(w, j)} value={contributionWineKey(w, j)}>
                      {wineLabel(w, j)}
                    </option>
                  ))}
                  {WINE_LETTERS.filter((l) => !wines.some((w) => w.letter === l)).map((l) => (
                    <option key={l} value={`letter:${l}`}>
                      {l} (no details)
                    </option>
                  ))}
                </select>
              </L>
              {SCORE_PARTS.map((f) => {
                const issue = scoreIssue(s[f.key], f.range);
                return (
                  <L key={f.key} label={f.label}>
                    <input
                      className={invalidField(issue)}
                      type="number"
                      inputMode="decimal"
                      step="0.25"
                      min={f.range.min}
                      max={f.range.max}
                      placeholder={`${f.range.min}–${f.range.max}`}
                      value={show(s[f.key])}
                      disabled={disabled}
                      aria-invalid={issue ? true : undefined}
                      title={issue ?? undefined}
                      onChange={(e) => setScore(i, { [f.key]: num(e.target.value) } as Partial<ContributionScore>)}
                    />
                    {issue && <span className="mt-1 block text-[11px] text-danger">{issue}</span>}
                  </L>
                );
              })}
              {(() => {
                const issue = computed == null ? scoreIssue(s.total, TOTAL_RANGE, false) : null;
                return (
                  <L label="Total">
                    <input
                      className={invalidField(issue)}
                      type="number"
                      inputMode="decimal"
                      step="0.25"
                      min={TOTAL_RANGE.min}
                      max={TOTAL_RANGE.max}
                      placeholder={`${TOTAL_RANGE.min}–${TOTAL_RANGE.max}`}
                      value={computed != null ? String(computed) : show(s.total)}
                      disabled={disabled || computed != null}
                      aria-invalid={issue ? true : undefined}
                      title={issue ?? (computed != null ? 'Calculated from the three parts' : 'Enter a total if the parts are unknown')}
                      onChange={(e) => setScore(i, { total: num(e.target.value) })}
                    />
                    {issue && <span className="mt-1 block text-[11px] text-danger">{issue}</span>}
                  </L>
                );
              })()}
              <L label="Their notes">
                <input className="field" value={s.notes ?? ''} disabled={disabled} onChange={(e) => setScore(i, { notes: str(e.target.value) })} />
              </L>
              {!disabled && (
                <button type="button" className="pb-2 text-xs text-wine-300 hover:underline" onClick={() => setScore(i, null)}>
                  remove
                </button>
              )}
            </div>
          );
        })}
        {!disabled && (
          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn-ghost text-sm" onClick={addScore}>
              + Add a score
            </button>
            {attendees.length > 0 && wines.length > 0 && (
              <button type="button" className="btn-ghost text-sm" onClick={addGrid} title="One empty score row for every attendee × wine">
                + Add a row for everyone × every wine
              </button>
            )}
          </div>
        )}
      </Section>

      <Section title="Memories & sources" hint="Stories, who won, what people said — and where this information comes from">
        <L label="What do you remember?">
          <textarea className="field min-h-[100px]" value={p.narrative ?? ''} disabled={disabled} onChange={(e) => patch((prev) => ({ ...prev, narrative: str(e.target.value) }))} />
        </L>
        <div className="grid gap-3 sm:grid-cols-2">
          <L label="Source (e.g. my 1994 notebook, Bob's photo album)">
            <input className="field" value={p.sourceDescription ?? ''} disabled={disabled} onChange={(e) => patch((prev) => ({ ...prev, sourceDescription: str(e.target.value) }))} />
          </L>
          <L label="How sure are you?">
            <select className="field" value={p.confidence ?? ''} disabled={disabled} onChange={(e) => patch((prev) => ({ ...prev, confidence: (e.target.value || null) as HistoryContributionPayload['confidence'] }))}>
              <option value="">—</option>
              {CONTRIBUTION_CONFIDENCES.map((c) => (
                <option key={c} value={c}>
                  {CONFIDENCE_LABEL[c]}
                </option>
              ))}
            </select>
          </L>
        </div>
      </Section>

      <Section title="Files & photos" hint="Scorecards, menus, labels, spreadsheets, documents — anything">
        <FileDropzone contributionId={contributionId} files={files} disabled={disabled} />
      </Section>
    </div>
  );
}
